import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S12P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 12"
      />
      <DefaultSlide title="Agenda">
        <div style={{ fontSize: "0.75em" }}>
          <ul>
            <li>ExpressJS</li>
            <li>NestJS</li>
            <li>
              React Avanzado (asincrónico)
              <ul>
                <li>Ciclo de vida de un componente</li>
                <li>Estructura de un proyecto de React</li>
                <li>Manejo de estados en React</li>
                <li>Otros conceptos importantes</li>
              </ul>
            </li>

          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide title="ExpressJS" />
        <DefaultSlide title="ExpressJS">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Framework para Node.js</li>
              <li>Permite crear aplicaciones web y APIs de manera sencilla</li>
              <li>Proporciona un conjunto de herramientas y funcionalidades para manejar rutas, peticiones HTTP, middleware, etc.</li>
              <li>Se basa en el patrón de diseño MVC (Modelo-Vista-Controlador)</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de ExpressJS"
          language="javascript">
          {`const express = require('express');
const app = express();

app.get('/', (req, res) => {
  res.send('Hello World!');
});

app.listen(3000, () => {
  console.log('Server is running on port 3000');
});
`}
        </CodeSlide>
        <section>
          <a href="https://expressjs.com/" target="_blank" rel="noopener noreferrer">https://expressjs.com/</a>
        </section>

        <section>
          <p>Manos a la obra, creemos una carpeta nueva y añadamos un archivo <code>app.js</code> que servirá de punto de entrada de nuestra aplicación.</p>
        </section>

        <CodeSlide
          title="Basic routing"
          language="javascript">
          {`const express = require('express');
const app = express();
app.get('/', (req, res) => {
  res.send('Hello World!');
}
app.get('/about', (req, res) => {
  res.send('About us');
}
app.get('/user', (req, res) => {
  res.send('This is a user');
}
app.post('/user', (req, res) => {
  res.send('Create a user');
}
app.listen(3000, () => {
  console.log('Server is running on port 3000');
});`}
        </CodeSlide>
        <section>
          <p>Notaron el <code>.get</code> y el <code>.post</code>?</p>
          <p>Esto es un ejemplo de un endpoint de usuario.</p>
          <p>Nosotros trabajaremos bajo el modelo REST para la creación de endpoints.</p>
          <p className="fragment">coming soon...</p>
        </section>

        <DefaultSlide title="Static files">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>ExpressJS permite servir archivos estáticos (HTML, CSS, JS, imágenes, etc.)</li>
              <li>Se puede usar el middleware <code>express.static</code> para servir archivos estáticos desde un directorio específico</li>
              <li>Ejemplo: <code>app.use(express.static('public'))</code></li>
              <li>Esto servirá todos los archivos dentro de la carpeta <code>public</code></li>
              <li>Se puede acceder a los archivos estáticos a través de la URL correspondiente</li>
              <li>Ejemplo: <code>http://localhost:3000/imagen.png</code></li>
            </ul>
          </div>
        </DefaultSlide>

        <DefaultSlide title="Middleware">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Middleware son funciones que tienen acceso al objeto de solicitud (req), al objeto de respuesta (res) y a la siguiente función de middleware en el ciclo de solicitud-respuesta</li>
              <li>Se pueden usar para realizar tareas como la autenticación, el manejo de errores, el registro de solicitudes, etc.</li>
              <li>Se pueden agregar middleware a la aplicación usando el método <code>app.use()</code></li>
              <li>Esto registrará la URL de cada solicitud en la consola</li>
              <li>Ejemplo: </li>
            </ul>
            <pre><code className="hljs language-js">{`app.use((req, res, next) => {
  console.log('Request URL:', req.originalUrl); next();
})`}</code></pre>
          </div>
        </DefaultSlide>

        <DefaultSlide title="Middleware">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Se pueden agregar middleware a rutas específicas usando el método <code>app.get('/ruta', middleware)</code></li>
              <li>Ejemplo:</li>
            </ul>
            <pre><code className="hljs language-js">{`app.get('/user', (req, res, next) => {
  console.log('User route');
  next();
})`}</code></pre>
          </div>
        </DefaultSlide>

        <DefaultSlide title="req, res y next">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li><code>req</code>: objeto de solicitud que contiene información sobre la solicitud HTTP (cuerpo, parámetros, encabezados, etc.)</li>
              <li><code>res</code>: objeto de respuesta que se utiliza para enviar una respuesta al cliente</li>
              <li><code>next</code>: función que se llama para pasar el control al siguiente middleware en la pila</li>
              <li>Si no se llama a <code>next()</code>, la solicitud se quedará colgada y no se enviará una respuesta al cliente</li>
            </ul>
          </div>
        </DefaultSlide>

        <DefaultSlide title="View engines">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>ExpressJS permite usar motores de plantillas para renderizar vistas (HTML dinámico)</li>
              <li>Se pueden usar motores de plantillas como EJS, Pug, Handlebars, etc.</li>
              <li>Se puede configurar el motor de plantillas usando el método <code>app.set('view engine', 'ejs')</code></li>
              <li>Se pueden renderizar vistas usando el método <code>res.render('vista', {`datos`})</code></li>
              <li>Esto renderizará la vista <code>vista.ejs</code> y pasará los datos a la vista</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de motor de plantillas"
          language="javascript">
          {`const express = require('express');
const app = express();
const path = require('path');
app.set('view engine', 'ejs');
app.set('views', path.join(__dirname, 'views'));
app.get('/', (req, res) => {
  res.render('index', { title: 'Hello World' });
}
app.listen(3000, () => {
  console.log('Server is running on port 3000');
});`}
        </CodeSlide>
        <CodeSlide
          title="Ejemplo con handlebars"
          language="javascript">
          {`const express = require('express');
const app = express();
const path = require('path');
app.set('view engine', 'hbs');
app.set('views', path.join(__dirname, 'views'));
app.get('/', (req, res) => {
  res.render('index', { title: 'Hello World' });
}
app.listen(3000, () => {
  console.log('Server is running on port 3000');
});`}
        </CodeSlide>

        <section>
          <p>Noten que somos nosotros los responsables de brindar el orden y formato al proyecto.</p>
          <p>Esto incluye la organización de los archivos y la estructura del código.</p>
          <p>Por lo que es importante que sigamos una estructura de carpetas y archivos que nos permita mantener el código limpio y ordenado.</p>
          <p className="fragment">o bien... utilicemos un framework :)</p>
        </section>

        <section>
          <p>Express tiene un generador propio para proyectos que utilizan exclusivamente express</p>
          <p><a href="https://expressjs.com/en/starter/generator.html" target="_blank" rel="noopener noreferrer">https://expressjs.com/en/starter/generator.html</a></p>
          <img width={400} src="/images/S12/expressgenerator.png" alt="Express generator structure" />
        </section>
        <section>
          <p>No nos vamos a enfocar en el generador de Express</p>
          <p>En su lugar, vamos a centrarnos en NestJS</p>
        </section>
      </section>

      <section>
        <section data-background-color="#0f1315">
          <img src="/images/S12/nestjs.png" alt="NestJS logo" />
        </section>
        <DefaultSlide title="NestJS">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Framework para Node.js</li>
              <li>Se basa en TypeScript y utiliza el patrón de diseño MVC (Modelo-Vista-Controlador)</li>
              <li>Proporciona una arquitectura modular y escalable para crear aplicaciones web y APIs</li>
              <li>Se integra fácilmente con otras bibliotecas y frameworks como Express, Fastify, GraphQL, etc.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Modules">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los módulos son la unidad básica de organización en NestJS</li>
              <li>Un módulo es una clase que se encarga de agrupar componentes relacionados (controladores, servicios, etc.)</li>
              <li>Se definen usando el decorador <code>@Module()</code></li>
              <li>Un módulo puede importar otros módulos y exportar sus propios componentes</li>
            </ul></div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de módulo"
          language="typescript">
          {`import { Module } from '@nestjs/common';
import { UsersController } from './users.controller';
import { UsersService } from './users.service';
@Module({
  imports: [],
  controllers: [UsersController],
  providers: [UsersService],
  exports: [UsersService],
})
export class UsersModule {}
`}
        </CodeSlide>
        <DefaultSlide title="Controllers">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los controladores son responsables de manejar las solicitudes HTTP y devolver respuestas al cliente</li>
              <li>Se definen usando el decorador <code>@Controller()</code></li>
              <li>Un controlador puede tener varios métodos que manejan diferentes rutas y tipos de solicitudes (GET, POST, PUT, DELETE, etc.)</li>
              <li>Se pueden usar decoradores como <code>@Get()</code>, <code>@Post()</code>, <code>@Put()</code>, <code>@Delete()</code> para definir los métodos del controlador</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de controlador"
          language="typescript">
          {`import { Controller, Get, Post, Body } from '@nestjs/common';
import { UsersService } from './users.service';
@Controller('users')
export class UsersController {
  constructor(private readonly usersService: UsersService) {}
  @Get()
  getAllUsers() {
    return this.usersService.findAll();
  }
  @Post()
  createUser(@Body() user: any) {
    return this.usersService.create(user);
  }
  @Get(':id')
  getUserById(@Param('id') id: string) {
    return this.usersService.findById(id);
  }
  @Put(':id')
  updateUser(@Param('id') id: string, @Body() user: any) {
    return this.usersService.update(id, user);
  }
  @Delete(':id')
  deleteUser(@Param('id') id: string) {
    return this.usersService.delete(id);
  }
}
`}
        </CodeSlide>
        <DefaultSlide title="Services">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los servicios son clases que contienen la lógica de negocio de la aplicación</li>
              <li>Se definen usando el decorador <code>@Injectable()</code></li>
              <li>Un servicio puede ser inyectado en otros componentes (controladores, módulos, etc.)</li>
              <li>Se pueden usar decoradores como <code>@Inject()</code> para inyectar dependencias en el constructor del servicio</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de servicio"
          language="typescript">
          {`import { Injectable } from '@nestjs/common';
@Injectable()
export class UsersService {
  private users = [];
  findAll() {
    return this.users;
  }
  findById(id: string) {
    return this.users.find(user => user.id === id);
  }
  create(user: any) {
    this.users.push(user);
    return user;
  }
  update(id: string, user: any) {
    const index = this.users.findIndex(u => u.id === id);
    if (index !== -1) {
      this.users[index] = user;
      return user;
    }
    return null;
  }
  delete(id: string) {
    const index = this.users.findIndex(u => u.id === id);
    if (index !== -1) {
      const user = this.users[index];
      this.users.splice(index, 1);
      return user;
    }
    return null;
  }
}
`}
        </CodeSlide>
        <DefaultSlide title="Dependency Injection">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>La inyección de dependencias es un patrón de diseño que permite gestionar las dependencias entre componentes de manera eficiente</li>
              <li>En NestJS, se utiliza un contenedor de inyección de dependencias para gestionar la creación y el ciclo de vida de los componentes</li>
              <li>Esto permite desacoplar los componentes y facilita la prueba y el mantenimiento del código</li>
              <li>Se pueden usar decoradores como <code>@Inject()</code> para inyectar dependencias en el constructor de un componente</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Decorators">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los decoradores son funciones que se utilizan para modificar el comportamiento de una clase, método o propiedad</li>
              <li>Se utilizan ampliamente en NestJS para definir módulos, controladores, servicios, etc.</li>
              <li>Se pueden crear decoradores personalizados para agregar funcionalidades específicas a los componentes</li>
              <li>Ejemplo: <code>@Get()</code>, <code>@Post()</code>, <code>@Injectable()</code></li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Pipes">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los pipes son funciones que se utilizan para transformar o validar los datos de entrada en una solicitud</li>
              <li>Se pueden usar para validar los datos de entrada, transformar los datos a un formato específico, etc.</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de pipe"
          language="typescript">
          {`@Get(':id')
async findOne(
  @Param('id', new ParseIntPipe({ errorHttpStatusCode: HttpStatus.NOT_ACCEPTABLE }))
  id: number,
) {
  return this.catsService.findOne(id);
}`}
        </CodeSlide>
        <DefaultSlide title="Guards">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los guards son funciones que se utilizan para proteger las rutas y controlar el acceso a los recursos</li>
              <li>Se pueden usar para verificar la autenticación, la autorización, etc.</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de guard"
          language="typescript">
          {`@Post()
@Roles(['admin'])
async create(@Body() createCatDto: CreateCatDto) {
  this.catsService.create(createCatDto);
} `}
        </CodeSlide>
        <DefaultSlide title="Interceptors">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Los interceptores son funciones que se utilizan para interceptar y modificar las solicitudes y respuestas</li>
              <li>Se pueden usar para agregar encabezados, modificar el cuerpo de la respuesta, manejar errores, etc.</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de interceptor"
          language="typescript">
          {`@Injectable()
export class LoggingInterceptor implements NestInterceptor {
  intercept(context: ExecutionContext, next: CallHandler): Observable<any> {
    console.log('Before...');

    const now = Date.now();
    return next
      .handle()
      .pipe(
        tap(() => console.log('After... \${Date.now() - now}ms')),
      );
  }
}`}
        </CodeSlide>
        <DefaultSlide title="Nestjs Lifecycle">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>El ciclo de vida de un componente en NestJS se compone de varias etapas</li>
              <li>Se pueden usar hooks como <code>onModuleInit()</code>, <code>onModuleDestroy()</code>, etc.</li>
              <li>Esto permite ejecutar código en momentos específicos del ciclo de vida del componente</li>
              <a href="https://docs.nestjs.com/fundamentals/lifecycle-events" target="_blank" rel="noopener noreferrer">https://docs.nestjs.com/fundamentals/lifecycle-events</a>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <img width={700} src="/images/S12/lifecycle-events.png" alt="Life cycle events in nestjs" />
        </section>
        <section>
          <p>Ahora vamos a crear un proyecto de NestJS de ejemplo.</p>
          <p>To-do backend</p>
        </section>
        <DefaultSlide title="Requerimientos">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Crear nuevas tareas y almacenarlas en la base de datos.</li>
              <li>La BD debe ser relacional.</li>
              <li>Se puede visualizar, completar y eliminar tareas.</li>
              <li>Las tareas deben estar ligadas a un usuario.</li>
              <li>En esta clase se enviará el id del usuario en cada solicitud. La próxima clase veremos seguridad.</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>


      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de parte 1" />
    </>
  );
}
